import React from "react";
import theme from "../../../../styles/theme";

/* ============================================================================
   COST BANNER — CORELIA
   ----------------------------------------------------------------------------
   BANNIÈRE HAUTE POUR LA COLONNE GAUCHE
   - conserve uniquement :
     1. overline
     2. titre
     3. ligne d’indicateurs
     4. filtres / tags
     5. légende
   - composant piloté par props
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  green: theme?.colors?.text?.green || "#1A8C74",
};

/* ============================================================================
   STYLES
============================================================================ */

const S = {
  root: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 0,
    overflow: "hidden",
    boxSizing: "border-box",
    padding: "2px 0",
    marginTop:"0px",
    marginBottom:"-25px",
  },

  headerText: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
  },

  overline: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    lineHeight: 1.1,
    color: T.verySoft,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },

  title: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "1.03rem",
    lineHeight: 1.14,
    color: T.text,
    fontWeight: 600,
    minWidth: 0,
  },

  toolbarGrid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },

  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 18,
    flexWrap: "wrap",
    justifyContent: "flex-start",
    minWidth: 0,
  },

  toolbarCenter: {
    display: "flex",
    justifyContent: "center",
    minWidth: 0,
  },

  toolbarRight: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    flexWrap: "wrap",
    minWidth: 0,
  },

  pillsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    flexWrap: "nowrap",
  },

  metricWrap: {
    display: "flex",
    alignItems: "baseline",
    gap: 6,
  },

  metricLabel: {
    fontSize: "0.72rem",
    color: T.verySoft,
    lineHeight: 1,
  },

  metricValue: {
    fontSize: "0.82rem",
    lineHeight: 1,
    fontWeight: 600,
  },

  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    flexShrink: 0,
  },

  legendText: {
    fontSize: "0.72rem",
    color: T.verySoft,
  },

  pillButton: {
    height: 28,
    padding: "0 10px",
    borderRadius: 999,
    fontSize: "0.71rem",
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    background: "#ffffff",
  },
};

/* ============================================================================
   SOUS-COMPOSANTS
============================================================================ */

const MetricInline = ({ label, value, valueColor = T.text }) => (
  <div style={S.metricWrap}>
    <span style={S.metricLabel}>{label}</span>
    <span style={{ ...S.metricValue, color: valueColor }}>{value}</span>
  </div>
);

const PillSwitch = ({ value, onChange, options = [] }) => (
  <div style={S.pillsRow}>
    {options.map((option) => {
      const active = option === value;

      return (
        <button
          key={option}
          type="button"
          onClick={() => onChange?.(option)}
          style={{
            ...S.pillButton,
            border: `1px solid ${
              active
                ? "rgba(14,63,86,0.18)"
                : theme?.colors?.borders?.default || "#d6dee4"
            }`,
            background: active ? "rgba(14,63,86,0.05)" : "#ffffff",
            color: active ? T.corelia : T.soft,
          }}
        >
          {option}
        </button>
      );
    })}
  </div>
);

const LegendDot = ({ color, label }) => (
  <div style={S.legendItem}>
    <div style={{ ...S.legendDot, background: color }} />
    <span style={S.legendText}>{label}</span>
  </div>
);

/* ============================================================================
   MAIN
============================================================================ */

const CostBanner = ({
  overline = "Cost structure",
  title = "Matching des sous-éléments avec le catalogue de coûts Corelia",

  metrics = [],
  filterOptions = [],
  activeFilter,
  onFilterChange,
  legends = [],

  showToolbar = true,
  showMetrics = true,
  showFilters = true,
  showLegends = true,

  rootStyle = {},
  overlineStyle = {},
  titleStyle = {},
  toolbarStyle = {},
}) => {
  /* ==========================================================================
     SÉCURISATION DES DONNÉES
     --------------------------------------------------------------------------
     Le composant reste robuste même si un onglet fournit une valeur
     absente, nulle ou mal typée.
  ========================================================================== */
  const safeMetrics = Array.isArray(metrics) ? metrics : [];
  const safeFilterOptions = Array.isArray(filterOptions) ? filterOptions : [];
  const safeLegends = Array.isArray(legends) ? legends : [];

  const hasMetrics = showMetrics && safeMetrics.length > 0;
  const hasFilters = showFilters && safeFilterOptions.length > 0;
  const hasLegends = showLegends && safeLegends.length > 0;

  const shouldShowToolbar =
    showToolbar && (hasMetrics || hasFilters || hasLegends);

  return (
    <div style={{ ...S.root, ...rootStyle }}>
      <div style={S.headerText}>
        <div style={{ ...S.overline, ...overlineStyle }}>{overline}</div>
        <div style={{ ...S.title, ...titleStyle }}>{title}</div>
      </div>

      {shouldShowToolbar && (
        <div style={{ ...S.toolbarGrid, ...toolbarStyle }}>
          <div style={S.toolbarLeft}>
            {hasMetrics &&
              safeMetrics.map((item) => (
                <MetricInline
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  valueColor={item.valueColor || T.text}
                />
              ))}
          </div>

          <div style={S.toolbarCenter}>
            {hasFilters && (
              <PillSwitch
                value={activeFilter}
                onChange={onFilterChange}
                options={safeFilterOptions}
              />
            )}
          </div>

          <div style={S.toolbarRight}>
            {hasLegends &&
              safeLegends.map((item) => (
                <LegendDot
                  key={item.label}
                  color={item.color}
                  label={item.label}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CostBanner;